import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from "@mui/material";
import { useHistory } from "react-router-dom";
import {
    CartItemIdentifier,
    FieldSearchContainerValue,
    FieldSearchRecordSeriesValue,
    FieldSearchSetNameValue,
    FieldValidationAltCode,
    FieldValidationBarcode,
    useAddItemMutation,
    useGetItemsAdvancedSearchQuery,
    useGetItemsBasicSearchQuery,
    useGetUserInfoQuery,
    useUpdateItemMutation
} from "../../services/grace";
import { InventoryAddItemResponse, InventoryEditItem } from "../../model/Inventory";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';
import DynamicBaseItemForm, { DynamicBaseItemFormProps } from "./DynamicBaseItemForm";
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import { RootState } from "../../util/store";
import { FormikBag, withFormik } from "formik";
import * as Yup from "yup";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import { format } from "date-fns";
import CartItemDialog from "../../components/dialogs/CartItemDialog/CartItemDialog";
import { APIFormDef, APIFormFieldDef } from "../../model/User";
import {isValidDate, parseDate} from "../../util/date";
//redux
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { setOptionsContainer, setOptionsFile, setOptionsTape, 
  setSearchContainerFormText, setSearchTapeFormText, setSearchFileFormText, 
  setSelectedValuesContainerForm, setSelectedValuesFileForm, setSelectedValuesTapeForm } from "../../features/search/searchSlice";

export interface FormData extends InventoryEditItem {
    setNameValue: FieldSearchSetNameValue | null;
    recordSeriesValue: FieldSearchRecordSeriesValue | null;
    containerValue: FieldSearchContainerValue | null;
}

const emptyFormData:FormData = {
    id: "",
    scanId: "",
    barcode: "",
    altCode: "",
    type: "",

    status: "",
    statusDate: "",

    accountLevel1: "",
    accountLevel2: "",
    accountLevel3: "",

    addDate: "",
    destroyDate: "",
    contentsRangeStart: "",
    contentsRangeEnd: "",
    contentsStartDate: "",
    contentsEndDate: "",

    shortDescription: "",
    longDescription: "",
    contents: "",

    setName: "",

    recordSeries: "",
    recordSeriesId: "",
    recordSeriesCode: "",
    recordSeriesTitle: "",
    legalHold: false,

    containerId: "",
    containerBarcode: "",
    containerAltCode: "",

    customField1: "",
    customField2: "",
    customField3: "",
    customField4: "",
    customDate: "",

    permanent: false,

    setNameValue: null,
    recordSeriesValue: null,
    containerValue: null,
}

enum FieldTypes {
    Barcode = 'barcode',
    AltCode = 'altcode',
}

declare type ValidationFieldType = FieldTypes.Barcode | FieldTypes.AltCode;

interface SaveClick {
    type: string,
    timestamp: string,
}

export interface DynamicFormProps {
    formDefinition: APIFormDef;
    formType: "container" | "file" | "tape";
}

export default function AddDynamicItemForm(props: DynamicFormProps): JSX.Element {
    const { formDefinition, formType } = props;
    const history = useHistory();
    const dispatch = useAppDispatch();
    const [addItem] = useAddItemMutation();
    const [updateItem] = useUpdateItemMutation();
    const alertDispatch = useAlertDispatch();

    const { data: userData } = useGetUserInfoQuery();

    const [addFormValues] = useState<FormData>(emptyFormData);
    const [inProgress, setInProgress] = useState(false);

    const [addCartItems, setAddCartItems] = useState<Array<CartItemIdentifier>>([]);
    const [openCartForm, setOpenCartForm] = useState(false);

    const [saveClicked, setSaveClicked] = useState<SaveClick>({type: "", timestamp: ""});

    useEffect(() => {
        if (saveClicked.type !== "") {
            submitButtonRef.current?.click();
        }
    }, [saveClicked])

    const [formData, setFormData] = useState<FormData | null>(null);
    useEffect(() => {
        if (formData !== null) {
            setInProgress(true);
            const {containerValue, recordSeriesValue, setNameValue, ...rest} = formData;

            // Note - do not reset "inProgress" after a successful add/update as doing
            // so will cause the data to refresh (due to cache invalidation) which
            // in turn causes a rebuild of the entire form as we navigate back to
            // the inventory page. This causes a memory leak so should be avoided.
            if (formData.id === "") {
                addItem({
                    ...rest,
                    containerId: containerValue?.id ?? "",
                    containerBarcode: containerValue?.barcode ?? "",
                    containerAltCode: containerValue?.altCode ?? "",
                    recordSeries: recordSeriesValue?.recordSeriesId ?? "",
                    recordSeriesId: recordSeriesValue?.recordSeriesId ?? "",
                    recordSeriesCode: recordSeriesValue?.recordSeriesCode ?? "",
                    recordSeriesTitle: recordSeriesValue?.recordSeriesTitle ?? "",
                    setName: setNameValue?.setName ?? "",
                }).unwrap().then((result: InventoryAddItemResponse) => {
                    if (result.itemID === undefined) {
                        alertDispatch({
                            type: 'add',
                            payload: {
                                type: "general",
                                message: "Invalid server response when saving",
                            }
                        });
                    } else {
                        if (saveClicked.type === "cart") {
                            setAddCartItems([{objectCode: result.itemID, objectType: formData.type}]);
                            setOpenCartForm(true);
                        } else {
                            history.replace("/inventory");
                        }
                    }
                }).catch((error) => {
                    console.log(error);
                    const err = error as FetchBaseQueryError;

                    if (err.status === 401) {
                        alertDispatch({
                            type: 'add',
                            payload: {
                                type: "auth",
                                message: "Your session has expired",
                            }
                        });
                    } else {
                        alertDispatch({
                            type: 'add',
                            payload: {
                                type: "general",
                                message: "Error saving item - please try again",
                            }
                        });
                    }

                    setInProgress(false);
                });
            } else {
                updateItem({
                    ...rest,
                    containerId: containerValue?.id ?? "",
                    containerBarcode: containerValue?.barcode ?? "",
                    containerAltCode: containerValue?.altCode ?? "",
                    recordSeries: recordSeriesValue?.recordSeriesId ?? "",
                    recordSeriesId: recordSeriesValue?.recordSeriesId ?? "",
                    recordSeriesCode: recordSeriesValue?.recordSeriesCode ?? "",
                    recordSeriesTitle: recordSeriesValue?.recordSeriesTitle ?? "",
                    setName: setNameValue?.setName ?? "",
                }).unwrap().then((result) => {
                    history.replace("/inventory");
                }).catch((error) => {
                    console.log(error);
                    const err = error as FetchBaseQueryError;

                    if (err.status === 401) {
                        alertDispatch({
                            type: 'add',
                            payload: {
                                type: "auth",
                                message: "Your session has expired",
                            }
                        });
                    } else {
                        alertDispatch({
                            type: 'add',
                            payload: {
                                type: "general",
                                message: "Error saving item - please try again",
                            }
                        });
                    }

                    setInProgress(false);
                });
            }
        }
    }, [formData]);

    const handleCartAddItemsClose = () => {
        history.replace("/inventory");
    };

    const getRequestBody = (validationType: ValidationFieldType, id: string, type: string, data: FieldValidationBarcode | FieldValidationAltCode):string => {
        if (validationType === FieldTypes.Barcode) {
            return JSON.stringify({
                context: (id === undefined || id === "") ? type : id,
                field: 'barcode',
                value: data,
            });
        } else {
            if (id !== "") {
                return JSON.stringify({
                    context: id,
                    field: 'altCode',
                    value: data,
                });
            } else {
                return JSON.stringify({
                    field: 'altCode',
                    value: data,
                });
            }
        }
    }

    const testField = (field: APIFormFieldDef, value: string, name: string): boolean => {
        if (field && field.regex !== undefined && field.regex !== "") {
            try {
                const regex = new RegExp(field.regex)
                return regex.test(value);
            } catch {
                console.warn(`unable to test value of ${name} using regex: ${field.regex}`);
            }
        }

        // Tests pass by default, so we can deal with broken regexes
        return true;
    }

    const validationSchema = Yup.object().shape({
        destroyDate: Yup.date(),
        containerValue: Yup.object()
            .nullable()
            .test({
                name: "valid-containerValue",
                test: (value, { parent, createError }) => {
                    if (formType === 'file' && value === null) {
                        return createError({
                            message: "you must select a Container Barcode or tick Open file",
                        });
                    }
                    return true;
                }
            }),
        barcode: Yup.string()
            .required('you must specify a barcode')
            .test({
                name: "valid-barcode",
                test: async (value, { parent, createError }) => {
                    if (value === undefined || value.length === 0) {
                        return false; // A barcode must be specified
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "barcode")

                    if (field !== undefined && !testField(field, value, "barcode")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    const result = await fetch(
                        '/api/inventory/fieldvalidate',
                        {
                            method: 'POST',
                            body: getRequestBody(
                                FieldTypes.Barcode,
                                parent.id,
                                parent.type,
                                {
                                    barcode: value,
                                }),
                        });

                    const res = await result.json();
                    if (res.valid === true) {
                        return true;
                    } else {
                        return createError({
                            message: res.error,
                        });
                    }
                }
            }),
        altCode: Yup.string()
            .test({
                name: "valid-altCode",
                test: async (value, { parent, createError }) => {
                    const field = formDefinition.fields.find(f => f.fieldId === "altCode")

                    if (field !== undefined) {
                        if (field.regex !== undefined && field.regex !== "") {
                            if (value === undefined || value.length === 0) {
                                return false; // "Alt. code" won't match the regex so this should fail
                            } else  if (!testField(field, value, "altCode")) {
                                return createError({
                                    message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                                });
                            }
                        }
                    }

                    if (value === undefined || value.length === 0) {
                        return true; // "Alt. code" is not required at this point
                    }

                    const result = await fetch(
                        '/api/inventory/fieldvalidate',
                        {
                            method: 'POST',
                            body: getRequestBody(
                                FieldTypes.AltCode,
                                parent.id,
                                parent.type,
                                {
                                    altCode: value,
                                    accountLevel1: parent.accountLevel1,
                                    accountLevel2: parent.accountLevel2,
                                    accountLevel3: parent.accountLevel3,
                                }),
                        });

                    const res = await result.json();
                    if (res.valid === true) {
                        return true;
                    } else {
                        return createError({
                            message: res.error,
                        });
                    }
                }
            }),
        contentsRangeStart: Yup.string()
            .test({
                name: "valid-contentsRange",
                test: async (value, { parent, createError }) => {
                    if (value === undefined && parent.contentsRangeEnd === undefined) {
                        return true;
                    }

                    if ((value === undefined && parent.contentsRangeEnd !== undefined) || (value !== undefined && parent.contentsRangeEnd === undefined)) {
                        return createError({
                            message: "you must enter a start and end, not one or the other",
                        });
                    }

                    const body = {
                        field: 'contentsRangeStart',
                        value: {
                            contentsRangeStart: value,
                            contentsRangeEnd: parent.contentsRangeEnd,
                        }
                    };

                    const result = await fetch(
                        '/api/inventory/fieldvalidate',
                        {
                            method: 'POST',
                            body: JSON.stringify(body),
                        });

                    const res = await result.json();
                    if (res.valid === true) {
                        return true;
                    } else {
                        return createError({
                            message: res.error,
                        });
                    }
                }
            }),
        contentsRangeEnd: Yup.string(),
        contentsStartDate: Yup.date()
            .test({
                name: "valid-contentsDateRange",
                test: (value, { parent, createError }) => {
                    // Catch if value > parent, value undefined or parent undefined but not both
                    if (value === undefined && parent.contentsEndDate === undefined) {
                        return true; // It's ok for both to be empty
                    }

                    if ((value === undefined && parent.contentsEndDate !== undefined) || (value !== undefined && parent.contentsEndDate === undefined)) {
                        return createError({
                            message: "you must enter a start and end date, not one or the other",
                        });
                    }

                    if (value !== undefined && value > parent.contentsEndDate) {
                        return createError({
                            message: "start date must be less than or equal to end date",
                        });
                    }

                    return true;
                }
            }),
        contentsEndDate: Yup.date(),
        shortDescription: Yup.string()
            .test({
                name: "valid-shortDescription",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "shortDescription")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (!testField(field, value, "shortDescription")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        longDescription: Yup.string()
            .test({
                name: "valid-longDescription",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "longDescription")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (!testField(field, value, "longDescription")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        contents: Yup.string()
            .test({
                name: "valid-shortDescription",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "contents")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (!testField(field, value, "contents")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customDate: Yup.date(),
        customField1: Yup.string()
            .test({
                name: "valid-customField1",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField1")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField1")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customField2: Yup.string()
            .test({
                name: "valid-customField2",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField2")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField2")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customField3: Yup.string()
            .test({
                name: "valid-customField3",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField3")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField3")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
        customField4: Yup.string()
            .test({
                name: "valid-customField4",
                test: (value, { createError }) => {
                    if (value === undefined) {
                        return true;
                    }

                    const field = formDefinition.fields.find(f => f.fieldId === "customField4")

                    if (field === undefined) {
                        // Field is not in the definition so don't test it
                        return true;
                    }

                    if (value.length > 25) {
                        return createError({
                            message: "max length is 25 characters",
                        })
                    }

                    if (!testField(field, value, "customField4")) {
                        return createError({
                            message: field.regexDesc === "" ? "invalid value" : field.regexDesc,
                        })
                    }

                    return true;
                }
            }),
    });

    const handleSubmit = (values: FormData, formikBag: FormikBag<DynamicBaseItemFormProps, FormData>) => {
        const { setSubmitting } = formikBag;
        setFormData(values);
        setSubmitting(false);
    };

    const filterAccountTypeDispatchOptions = (
      type: "container" | "file" | "tape",
      value:
        | {
            id: string;
            label: string;
          }[]
        | null
    ) => {
      switch (type) {
        case "container":
          dispatch(setOptionsContainer(value));
          break;
        case "file":
          dispatch(setOptionsFile(value));
          break;
        case "tape":
          dispatch(setOptionsTape(value));
          break;
      }
    };

    const filterAccountTypeDispatchSearchText = (
      type: "container" | "file" | "tape",
      value: string | null
    ) => {
      switch (type) {
        case "container":
          dispatch(setSearchContainerFormText(value));
          break;
        case "file":
          dispatch(setSearchFileFormText(value));
          break;
        case "tape":
          dispatch(setSearchTapeFormText(value));
          break;
      }
    };

    const filterAccountTypeDispatchSelectedValues = (
      type: "container" | "file" | "tape",
      value: {
        level1: string;
        level2: string;
        level3: string;
      } | null
    ) => {
      switch (type) {
        case "container":
          dispatch(setSelectedValuesContainerForm(value));
          break;
        case "file":
          dispatch(setSelectedValuesFileForm(value));
          break;
        case "tape":
          dispatch(setSelectedValuesTapeForm(value));
          break;
      }
    };

    const handleClose = () => {
        filterAccountTypeDispatchOptions(formType,null);
        filterAccountTypeDispatchSearchText(formType, null);
        filterAccountTypeDispatchSelectedValues(formType, null);
        history.replace("/inventory");
    }

    // Keep a link to the inventory data so that it stays cached. Otherwise, the inventory grid will
    // reload if we return to that page after the data has expired (by default this is 60 seconds)
    // -- START CACHING --
    const basicSearch = useAppSelector((state: RootState) => state.inventory.basicSearch);
    const advancedSearch = useAppSelector((state: RootState) => state.inventory.advancedSearch);
    useGetItemsBasicSearchQuery(basicSearch.params, { skip: !basicSearch.active });
    useGetItemsAdvancedSearchQuery(advancedSearch.params, { skip: !advancedSearch.active });
    // -- END CACHING --

    const submitButtonRef = useRef<HTMLButtonElement>(null);

    const FormikDynamicAddItemForm = useMemo(() => withFormik<DynamicBaseItemFormProps, FormData>({

        validateOnBlur: false,
        validateOnChange: false,

        validationSchema: validationSchema,

        mapPropsToValues: (props) => {
            const values = {...props.formValues}

            const currentDate = format(new Date(),"yyyy-MM-dd");
            values.type = formType;
            values.status = "pending";
            values.statusDate = currentDate;
            values.addDate = currentDate;

            let field = formDefinition.fields.find(f => f.fieldId === "customField1")
            if (field !== undefined && field.defaultValue !== "") {
               values.customField1 = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "customField2")
            if (field !== undefined && field.defaultValue !== "") {
                values.customField2 = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "customField3")
            if (field !== undefined && field.defaultValue !== "") {
                values.customField3 = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "customField4")
            if (field !== undefined && field.defaultValue !== "") {
                values.customField4 = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "customDate")
            if (field !== undefined && field.defaultValue !== "" && parseDate(field.defaultValue) !== null) {
                values.customDate = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "shortDescription")
            if (field !== undefined && field.defaultValue !== "") {
                values.shortDescription = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "longDescription")
            if (field !== undefined && field.defaultValue !== "") {
                values.longDescription = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "contents")
            if (field !== undefined && field.defaultValue !== "") {
                values.contents = field.defaultValue;
            }

            field = formDefinition.fields.find(f => f.fieldId === "permanent")
            if (field !== undefined && field.defaultValue !== "") {
                values.permanent = Boolean(field.defaultValue);
            } else {
                values.permanent = false;
            }

            values.containerValue = null;
            values.recordSeriesValue = null;
            values.setNameValue = null;

            return values;
        },

        handleSubmit: handleSubmit,
    })(DynamicBaseItemForm), []);

    const itemTypeLabel = formType === "container" ? "carton" : formType;

    return (
        <Box>
            <GDGAppBar title={`Add ${itemTypeLabel}`} drawerWidth={0} onClose={handleClose}>
                {
                    inProgress &&
                    <CircularProgress color="secondary" size={20}/>
                }
                <GDGAppBarButton
                  disabled={inProgress}
                  onClick={() => {
                      setSaveClicked({type: "cart", timestamp: (new Date()).toISOString()});
                  }}
                  text="Save & add to cart"
                />

                <GDGAppBarButton
                  disabled={inProgress}
                  onClick={() => {
                      setSaveClicked({type: "save", timestamp: (new Date()).toISOString()});
                  }}
                  text="Save"
                />
            </GDGAppBar>
            <FormikDynamicAddItemForm
              type={formType}
              disabled={inProgress}
              submitButtonRef={submitButtonRef}
              formDefinition={formDefinition}
              formValues={addFormValues}
            />
            <CartItemDialog
                open={openCartForm}
                type="item"
                mode="add"
                cartItems={addCartItems}
                itemTypes={userData?.serviceTypes !== undefined ? userData?.serviceTypes : []}
                onClose={handleCartAddItemsClose}
                onSubmit={handleCartAddItemsClose}
            />
        </Box>
    )
}
